import { Tooltip } from '@mui/material';
import LoadableResource from 'util/resource/Resource';
import TeamMember from './TeamMember';
import React, { useState } from 'react';
import { companiesApi, HighlightedSection } from 'apis/CompanyAPI/companies/companiesApi';
import Card from 'ui/views/cards/Card';
import Button from 'ui/elements/buttons/Button';
import { ICompany } from 'types/company';
import useLazyResource from 'util/resource/useLazyResource';
import SectionHeading from 'ui/elements/SectionHeading';
import EditAction from 'ui/elements/actions/EditAction';
import styled from '@emotion/styled';
import { halfSpacing, sectionSpacing } from 'ui/theme/themeConstants';

// Custom responsive grid that adapts to container width instead of screen width
const ResponsiveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${halfSpacing};
  margin-bottom: ${sectionSpacing};
`;

import IconButton from 'ui/elements/icons/IconButton';
import ChevronUpIcon from 'ui/elements/icons/ChevronUpIcon';
import ChevronDownIcon from 'ui/elements/icons/ChevronDownIcon';
import { moveDown, moveUp } from 'util/arrayUtils';

import meetTheTeamIllustration from 'ui/illustrations/meet_the_team.svg';
import HighlightedUsersDialog, { EditSectionMode } from './HighlightedUsersDialog';
import { useCompanyLastUpdated } from '../LastUpdated';
import useHighlightedUsers from './useHighlightedUsers';

interface Props {
  company: ICompany;
  onOpenTeamMember: (cwUserId: UUID) => void;
  canEditCompany?: boolean;
}

export default function TeamMembers(props: Props) {
  const { resource: highlightedUsersResource, mutate } = useHighlightedUsers(props.company.slug);
  const { setLastUpdated } = useCompanyLastUpdated(props.company.id);

  const [editMode, setEditMode] = useState<EditSectionMode | undefined>(undefined);

  const [patchSectionOrder, isSaving] = useLazyResource(
    (ordered: HighlightedSection[]) => {
      const promise = companiesApi.profile.highlightedUsers.patchOrder(
        props.company.slug,
        ordered.map(section => section.id),
      );
      mutate(promise, { optimisticData: { sections: ordered } });
      return promise;
    },
    {
      onSuccess: () => {
        setLastUpdated(new Date());
      },
    },
  );

  return (
    <>
      <LoadableResource resource={highlightedUsersResource}>
        {result => {
          // Only show sections that have at least one user
          const sectionsWithUsers = result.sections.filter(section => section.users.length > 0);
          
          return (
            <div>
              {sectionsWithUsers.map((section, i) => (
                <div key={section.id} data-intercom-target={`highlighted-users-${section.sectionName}`}>
                  <SectionHeading heading={section.sectionName} addMobileMargin>
                    {props.canEditCompany && (
                      <>
                        {i > 0 && (
                          <Tooltip title="Move section up">
                            {/* tooltip doesn't work with IconButton as a child */}
                            <span>
                              <IconButton
                                disabled={isSaving}
                                size="small"
                                onClick={() => patchSectionOrder(moveUp(result.sections, section, s => s.id))}
                              >
                                <ChevronUpIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {i < sectionsWithUsers.length - 1 && (
                          <Tooltip title="Move section down">
                            {/* tooltip doesn't work with IconButton as a child */}
                            <span>
                              <IconButton
                                disabled={isSaving}
                                size="small"
                                onClick={() => patchSectionOrder(moveDown(result.sections, section, s => s.id))}
                              >
                                <ChevronDownIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <button onClick={() => setEditMode({ type: 'edit', section })}>
                          <EditAction tooltip="Edit" />
                        </button>
                      </>
                    )}
                  </SectionHeading>

                  <ResponsiveGrid>
                    {section.users.map(user => (
                      <TeamMember
                        key={user.id}
                        user={user}
                        companyId={props.company.id}
                        onClick={props.onOpenTeamMember}
                      />
                    ))}
                  </ResponsiveGrid>
                </div>
              ))}

              {props.canEditCompany && sectionsWithUsers.length === 0 && (
                <Card
                  onClick={() => setEditMode({ type: 'new' })}
                  hover="highlight"
                  type="dashed"
                  className="u-content-spacing-y text-body u-flex u-flex-space-around"
                  data-intercom-target="highlighted-users-cta"
                >
                  <div className="u-flex u-flex--column u-flex-align-center">
                    <img style={{ maxWidth: '15rem' }} src={meetTheTeamIllustration} />
                    <p className="u-content-spacing-top">
                      Showcase the key stakeholders in your company, such as board members, key employees and lead
                      investors.
                    </p>
                    <Button kind="tertiary">Add stakeholders</Button>
                  </div>
                </Card>
              )}

              {props.canEditCompany && sectionsWithUsers.length > 0 && (
                <Card
                  onClick={() => setEditMode({ type: 'new' })}
                  hover="highlight"
                  type="dashed"
                  className="u-content-spacing-y text-body u-flex u-flex-space-around"
                  data-intercom-target="highlighted-users-add-section"
                >
                  <Button kind="tertiary">Add another section</Button>
                </Card>
              )}
              {editMode && (
                <HighlightedUsersDialog
                  editMode={editMode}
                  onClose={() => setEditMode(undefined)}
                  onSuccess={response => mutate(response)}
                  company={props.company}
                />
              )}
            </div>
          );
        }}
      </LoadableResource>
    </>
  );
}
